import { useState } from "react";
import { Link } from "react-router-dom";
import { PILLARS, pillarById } from "@/content/pillars";
import { programmeById } from "@/content/programmes";
import { productById } from "@/content/products";
import { articleBySlug } from "@/content/articles";

const CX = 450;
const CY = 450;
const R = 268;
const RING = 372;

const pos = (i, r) => {
  const a = ((i * 45 - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
};

const anchorFor = (i) => {
  const [x] = pos(i, R);
  if (x > CX + 40) return "start";
  if (x < CX - 40) return "end";
  return "middle";
};

export default function SystemDiagram() {
  const [active, setActive] = useState("nutrition");
  const pillar = pillarById(active) || PILLARS[0];
  const related = pillar.related.map(pillarById).filter(Boolean);

  return (
    <div className="grid items-start gap-10 lg:grid-cols-[1.25fr_1fr]">
      <div className="mx-auto w-full max-w-[640px]">
        <svg
          viewBox="0 0 900 900"
          className="h-auto w-full"
          role="img"
          aria-label="The W8ED system: eight pillars of human health connected through time"
          data-testid="system-diagram"
        >
          {/* TIME — the outer orbit, not a pillar */}
          <g className="time-orbit" style={{ transformOrigin: "450px 450px" }}>
            <circle cx={CX} cy={CY} r={RING} fill="none" stroke="#2e2b24" strokeWidth="1" strokeDasharray="2 12" />
          </g>
          {["TIME", "TIME", "TIME", "TIME"].map((t, i) => {
            const [tx, ty] = pos(i * 2, RING + 4);
            return (
              <text
                key={i}
                x={tx}
                y={ty}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#57534a"
                fontSize="11"
                letterSpacing="8"
                fontFamily="JetBrains Mono, monospace"
              >
                {t}
              </text>
            );
          })}

          {/* interconnection ring + spokes */}
          <circle cx={CX} cy={CY} r={R} fill="none" stroke="#1f1e1a" strokeWidth="1" />
          {PILLARS.map((p, i) => {
            const [x, y] = pos(i, R);
            return <line key={p.id} x1={CX} y1={CY} x2={x} y2={y} stroke="#1f1e1a" strokeWidth="1" />;
          })}

          {/* active pillar relations */}
          {related.map((rel) => {
            const [x1, y1] = pos(PILLARS.findIndex((p) => p.id === pillar.id), R);
            const [x2, y2] = pos(PILLARS.findIndex((p) => p.id === rel.id), R);
            return (
              <line
                key={rel.id}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#C9A227"
                strokeWidth="1"
                strokeOpacity="0.4"
              />
            );
          })}

          {/* center */}
          <circle cx={CX} cy={CY} r="70" fill="#0a0a09" stroke="#2a2822" strokeWidth="1" />
          <text x={CX} y={CY - 8} textAnchor="middle" fill="#f5f2ea" fontSize="17" fontWeight="800" letterSpacing="3" fontFamily="Archivo, sans-serif">
            HUMAN
          </text>
          <text x={CX} y={CY + 14} textAnchor="middle" fill="#f5f2ea" fontSize="17" fontWeight="800" letterSpacing="3" fontFamily="Archivo, sans-serif">
            HEALTH
          </text>

          {/* pillar nodes */}
          {PILLARS.map((p, i) => {
            const [x, y] = pos(i, R);
            const [lx, ly] = pos(i, R + 44);
            const isActive = p.id === pillar.id;
            const isRelated = related.some((r) => r.id === p.id);
            const labelFill = isActive ? "#C9A227" : isRelated ? "#b8b3a6" : "#8e8a80";
            return (
              <g
                key={p.id}
                role="button"
                tabIndex={0}
                aria-label={`${p.name} — pillar ${p.number} of 08`}
                data-testid={`pillar-node-${p.id}`}
                className="cursor-pointer focus:outline-none"
                onMouseEnter={() => setActive(p.id)}
                onClick={() => setActive(p.id)}
                onFocus={() => setActive(p.id)}
              >
                <circle cx={x} cy={y} r="28" fill="transparent" />
                <circle
                  cx={x}
                  cy={y}
                  r={isActive ? 8 : 5}
                  fill={isActive ? "#C9A227" : "#3a382f"}
                  style={{ transition: "all 300ms" }}
                />
                <text
                  x={lx}
                  y={ly - 10}
                  textAnchor={anchorFor(i)}
                  fill={isActive ? "#C9A227" : "#57534a"}
                  fontSize="10"
                  letterSpacing="2"
                  fontFamily="JetBrains Mono, monospace"
                  style={{ transition: "fill 300ms" }}
                >
                  {p.number} / 08
                </text>
                <text
                  x={lx}
                  y={ly + 8}
                  textAnchor={anchorFor(i)}
                  fill={labelFill}
                  fontSize="13"
                  letterSpacing="3"
                  fontFamily="JetBrains Mono, monospace"
                  style={{ transition: "fill 300ms" }}
                >
                  {p.name}
                </text>
              </g>
            );
          })}
        </svg>

        <p className="mt-6 text-center font-mono text-[10px] tracking-[0.3em] text-[#57534a]">
          8 PILLARS <span className="text-[#C9A227]">×</span> TIME{" "}
          <span className="text-[#C9A227]">→</span> ADAPTATION{" "}
          <span className="text-[#C9A227]">→</span> HUMAN HEALTH
        </p>
      </div>

      <aside data-testid="pillar-detail" className="border border-[#1f1e1a] p-8 lg:p-10">
        <p className="font-mono text-[10px] tracking-[0.35em] text-[#C9A227]">
          SIGNAL {pillar.number} / 08
        </p>
        <h3 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#f5f2ea]">
          {pillar.name}
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-[#b8b3a6]">{pillar.short}</p>

        <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">WHY IT MATTERS</p>
        <p className="mt-2 text-sm leading-relaxed text-[#8e8a80]">{pillar.why}</p>

        <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">CONNECTED TO</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {related.map((r) => (
            <button
              key={r.id}
              type="button"
              data-testid={`related-${r.id}`}
              onClick={() => setActive(r.id)}
              className="border border-[#26241d] px-3 py-1.5 font-mono text-[9px] tracking-[0.2em] text-[#8e8a80] transition-colors hover:border-[#C9A227] hover:text-[#C9A227]"
            >
              {r.name}
            </button>
          ))}
        </div>

        {(pillar.thinking.length > 0 || pillar.programmes.length > 0 || pillar.products.length > 0) && (
          <>
            <p className="mt-8 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">IN THE SYSTEM</p>
            <ul className="mt-3 space-y-2">
              {pillar.thinking.map((slug) => (
                <li key={slug}>
                  <Link
                    to={`/thinking/${slug}`}
                    className="font-mono text-[10px] tracking-[0.15em] text-[#8e8a80] transition-colors hover:text-[#C9A227]"
                  >
                    THINKING — {articleBySlug(slug)?.title || slug}
                  </Link>
                </li>
              ))}
              {pillar.programmes.map((id) => (
                <li key={id}>
                  <Link
                    to={`/programmes#${id}`}
                    className="font-mono text-[10px] tracking-[0.15em] text-[#8e8a80] transition-colors hover:text-[#C9A227]"
                  >
                    PROGRAMME — {programmeById(id)?.name || id}
                  </Link>
                </li>
              ))}
              {pillar.products.map((id) => (
                <li key={id}>
                  <Link
                    to={`/products#${id}`}
                    className="font-mono text-[10px] tracking-[0.15em] text-[#8e8a80] transition-colors hover:text-[#C9A227]"
                  >
                    PRODUCT — {productById(id)?.name || id}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        <Link
          to={`/science#${pillar.id}`}
          data-testid="pillar-explore-link"
          className="mt-10 inline-block border border-[#3a3524] px-5 py-3 font-mono text-[10px] tracking-[0.25em] text-[#C9A227] transition-colors hover:border-[#C9A227]"
        >
          EXPLORE {pillar.name} →
        </Link>
      </aside>
    </div>
  );
}
